import {Auth} from 'aws-amplify';

import {AxiosResponse} from 'axios';
import getFlags from '../../flagGetter';
import {User, Account, Scope, PartnerType, ObjectOfAny} from '../../types';
import {JWT_ERROR, UPDATE_USER_EVENT} from '../../constants';
import SystemOfRecordService, {SOREndpoints} from './SystemOfRecordService';
import setUserAuthToken from './setUserAuthToken';

export interface AWSError {
  code: string;
  name: string;
  message: string;
}

// @todo create types for the response
export const getAccountFromCognitoUserResponse = (response: ObjectOfAny): Account => {
  const {attributes, ChallengeName, ChallengeParameters, signInUserSession} = response;

  // when the user has a challenge, they don't have attributes
  // everything comes back as part of the challenge
  const userAttributes = attributes || JSON.parse(ChallengeParameters.userAttributes);
  const cognitoGroups = signInUserSession?.accessToken?.payload?.['cognito:groups'] || [];

  if (!signInUserSession?.accessToken?.jwtToken) {
    throw new Error(JWT_ERROR);
  }

  return {
    challengeName: ChallengeName,
    email: userAttributes.email,
    emailVerified: userAttributes.email_verified === true || userAttributes.email_verified === 'true',
    jwt: signInUserSession?.accessToken?.jwtToken,
    // advisors also have a customer scope, so for now we're ensuring users only have one scope
    scopes: cognitoGroups.includes('advisor') ? [Scope.ADVISOR] : [Scope.CUSTOMER],
  };
};

export interface ChangePassword {
  newPassword: string;
  oldPassword: string;
}

const changePassword = ({newPassword, oldPassword}: ChangePassword): Promise<'SUCCESS'> => {
  return Auth.currentAuthenticatedUser().then((user) => {
    return Auth.changePassword(user, oldPassword, newPassword);
  });
};

export interface ConfirmEmailAddress {
  confirmationCode: string;
}

const confirmEmailAddress = ({confirmationCode}: ConfirmEmailAddress): Promise<User> => {
  return Auth.verifyCurrentUserAttributeSubmit('email', confirmationCode).then(async () => {
    const updatedUser: User = await Auth.currentAuthenticatedUser({bypassCache: true});
    return updatedUser;
  });
};

export interface GetUserByEmail {
  confirmationCode: string;
  emailAddress: string;
  params?: {
    forceAliasCreation?: boolean;
  };
}

const getUserByEmail = ({emailAddress, confirmationCode, params}: GetUserByEmail): Promise<ObjectOfAny> => {
  return Auth.confirmSignUp(emailAddress, confirmationCode, params);
};

export interface CompleteNewPassword {
  password: string;
}

const completeNewPassword = ({password}: CompleteNewPassword): Promise<ObjectOfAny> => {
  const requiredAttributes = {};
  return Auth.currentAuthenticatedUser({bypassCache: true}).then((user) => {
    return Auth.completeNewPassword(user, password, requiredAttributes);
  });
};

const getProfile = (): Promise<AxiosResponse<SystemOfRecord.AccountHolderDetails>> => {
  return SystemOfRecordService.get<SystemOfRecord.AccountHolderDetails>(SOREndpoints.CUSTOMER_PROFILE);
};

const getPartnerProfile = (): Promise<AxiosResponse<SystemOfRecord.PartnerModelAccount>> => {
  return SystemOfRecordService.get<SystemOfRecord.PartnerModelAccount>(SOREndpoints.ADVISOR_PROFILE);
};

export const emitUpdateUserEvent = (userData: User | undefined): void => {
  // trigger update in IdentityContext
  window.dispatchEvent(new CustomEvent(UPDATE_USER_EVENT, {detail: userData}));
};

const updateProfile = async (
  accountData: Partial<SystemOfRecord.AccountHolderDetails>
): Promise<AxiosResponse<SystemOfRecord.AccountHolderDetails>> => {
  const response = await SystemOfRecordService.post<SystemOfRecord.AccountHolderDetails>(
    SOREndpoints.CUSTOMER_PROFILE,
    accountData
  );

  return response;
};

const updatePartnerProfile = async (
  partnerData: Partial<SystemOfRecord.PartnerModelAccount>
): Promise<AxiosResponse<SystemOfRecord.PartnerModelAccount>> => {
  const response = await SystemOfRecordService.post<SystemOfRecord.PartnerModelAccount>(
    SOREndpoints.ADVISOR_PROFILE,
    partnerData
  );

  return response;
};

export interface Login {
  emailAddress: string;
  password: string;
}

const login = async ({emailAddress, password}: Login): Promise<Account> => {
  const response = await Auth.signIn(emailAddress, password);
  const userAccount = getAccountFromCognitoUserResponse(response);
  setUserAuthToken(userAccount.jwt);
  return userAccount;
};

const logout = async (): Promise<void> => {
  await Auth.signOut({global: true});
};

const resendConfirmEmail = (): Promise<void> => {
  return Auth.verifyCurrentUserAttribute('email');
};

export interface CreateAccount {
  emailAddress: string;
  firstName?: string;
  marketingOptIn: boolean;
  password: string;
  surname?: string;
  telephone?: string;
  isAdvisor?: boolean;
}

export type CreateAdvisorAccount = CreateAccount & {
  companyNumber?: string;
  companyName?: string;
  partnerType?: PartnerType | '';
  // @todo: advisor type field
  // advisorType: AdvisorType | '';
};

const createAccount = async ({
  emailAddress,
  firstName,
  surname,
  marketingOptIn,
  password,
  telephone,
  isAdvisor,
  companyNumber,
  companyName,
  partnerType,
}: CreateAdvisorAccount): Promise<User> => {
  // @todo this typing could be better
  const FLAGS = getFlags();

  // create cognito account
  await Auth.signUp({
    username: emailAddress,
    password,
    clientMetadata: {
      isAdvisor: isAdvisor ? 'true' : 'false', // only accepts a string
    },
  });

  // login automatically
  const userAccount = await login({emailAddress, password});
  // we need to manually trigger the verification email since auto-verification is turned on in Cognito
  // this will only work _after_ the login has happened
  if (FLAGS.SEND_VERIFY_EMAIL) {
    resendConfirmEmail();
  }

  // set the customer information before we set the partner information
  const userProfileData: SystemOfRecord.AccountHolderDetails = {
    first_name: firstName,
    last_name: surname,
    email: emailAddress,
    mobile_number: telephone,
    marketing_opt_in: marketingOptIn,
  };

  // create new user profile in System of Record
  const userProfile = await updateProfile(userProfileData);

  // if signing up as an advisor, also create new partner profile in System of
  let partnerProfile;

  if (isAdvisor) {
    const partnerProfileData: SystemOfRecord.PartnerModelAccount = {
      company_name: companyName,
      company_number: companyNumber,
      partner_type: partnerType || PartnerType.ADVISORY_ACCOUNT,
    };
    partnerProfile = await updatePartnerProfile(partnerProfileData);
  }

  const userData: User = {
    ...userAccount,
    ...userProfile.data,
    partner: {
      ...partnerProfile?.data,
    },
  };

  // trigger update in IdentityContext
  emitUpdateUserEvent(userData);

  return userData;
};

export interface ForgotPassword {
  emailAddress: string;
}

const forgotPassword = ({emailAddress}: ForgotPassword): Promise<void> => {
  return Auth.forgotPassword(emailAddress);
};

const getAccount = async (): Promise<Account | undefined> => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    return getAccountFromCognitoUserResponse(user);
  } catch (e) {
    // falls into here when no user is logged in
    return undefined;
  }
};

export interface ResetPassword {
  confirmationCode: string;
  emailAddress: string;
  password: string;
}

const resetPassword = ({confirmationCode, emailAddress, password}: ResetPassword): Promise<void> => {
  return Auth.forgotPasswordSubmit(emailAddress, confirmationCode, password);
};

const Identity = {
  changePassword,
  completeNewPassword,
  confirmEmailAddress,
  createAccount,
  forgotPassword,
  getAccount,
  getPartnerProfile,
  getProfile,
  getUserByEmail,
  login,
  logout,
  resendConfirmEmail,
  resetPassword,
  updatePartnerProfile,
  updateProfile,
};

export default Identity;
